import React from "react"

import AddIcon from "@mui/icons-material/Add"
import { Box, Button } from "@mui/material"
import type { GridColDef, GridFilterItem } from "@mui/x-data-grid-pro"
import {
  GridFilterPanel,
  gridFilterModelSelector,
  gridFilterableColumnDefinitionsSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from "@mui/x-data-grid-pro"

const filterColumns = ({
  columns,
  currentFilters,
  field,
}: {
  columns: GridColDef[]
  currentFilters: GridFilterItem[]
  field: GridColDef["field"]
}): GridColDef["field"][] => {
  const filteredFields = new Set(
    currentFilters
      .map((item) => item.field)
      .filter((itemField) => itemField !== field)
  )

  return columns
    .filter(
      (column) =>
        column.filterable &&
        (column.field === field || !filteredFields.has(column.field))
    )
    .map((column) => column.field)
}

const getColumnForNewFilter = ({
  columns,
  currentFilters,
}: {
  columns: GridColDef[]
  currentFilters: GridFilterItem[]
}): GridColDef["field"] | null => {
  const filteredFields = new Set(currentFilters.map((item) => item.field))
  const nextColumn = columns
    .filter((column) => column.filterable && !filteredFields.has(column.field))
    .find((column) => (column.filterOperators?.length ?? 0) > 0)

  return nextColumn?.field ?? null
}

interface DataGridFilterPanelProps {
  disableAddFilterButton?: boolean
  disableRemoveAllButton?: boolean
  filterFormProps?: {
    columnsSort?: "asc" | "desc"
    logicOperatorInputProps?: {
      as?: React.ElementType
      itemId?: string
    }
  }
  ref?: React.Ref<HTMLDivElement>
}

/**
 * Filter panel that allows each column once. Add filter is disabled once
 * every filterable column is already in use.
 */
export const DataGridFilterPanel = ({
  disableAddFilterButton = false,
  disableRemoveAllButton = false,
  filterFormProps,
  ref,
}: DataGridFilterPanelProps) => {
  const apiRef = useGridApiContext()
  const { localeText } = useGridRootProps()
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector)
  const columns = useGridSelector(
    apiRef,
    gridFilterableColumnDefinitionsSelector
  )
  const canAddFilter =
    getColumnForNewFilter({
      columns,
      currentFilters: filterModel.items,
    }) != null

  return (
    <Box ref={ref} sx={{ position: "relative" }}>
      <GridFilterPanel
        disableAddFilterButton={!canAddFilter || disableAddFilterButton}
        disableRemoveAllButton={disableRemoveAllButton}
        filterFormProps={{
          filterColumns,
          ...filterFormProps,
        }}
        getColumnForNewFilter={getColumnForNewFilter}
      />
      {canAddFilter ? null : (
        <Button
          disabled
          startIcon={<AddIcon />}
          sx={{ bottom: 1, left: 1, position: "absolute" }}
        >
          {localeText.filterPanelAddFilter}
        </Button>
      )}
    </Box>
  )
}
